import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ClaimsPanel } from "@/components/ClaimsPanel";
import { Link } from "react-router-dom";
import { STAGE_LABEL } from "@/constants/roles";
import { toast } from "sonner";
import { useT } from "@/i18n/LanguageContext";
import { alertHeadline, alertTypeLabel } from "@/i18n/alerts";
import { IssuePhotos } from "@/components/IssuePhotos";
import { HoldRespondButtons, QcResolveButton, ReorderDecideButtons, StoreAckButton, HOLD_RESPONDER_ROLES, canDecideReorder, canAckStore } from "@/components/HoldRespondDialog";
import { CancelStopButton, canConfirmCancelStop } from "@/components/CancelStockStopDialog";
import { HoldStopButton, canAckHoldStop } from "@/components/HoldStopDialog";
import { ExtensionDecideButtons, canDecideExtension } from "@/components/ProductionExtensionDialog";
import { InvPackingKpis } from "@/components/InvPackingKpis";
import { RemindersSquare } from "@/components/RemindersSquare";

const ROLE_DASHBOARD_STAGES = {
  factory_1: ["pending", "advance", "production", "product_transfer", "completed"],
  factory_2: ["pending", "advance", "production", "product_transfer", "completed"],
  factory_3: ["pending", "advance", "production", "product_transfer", "completed"],
  packing_orders: ["product_transfer", "packing"],
  inventory_packing: ["inventory_packing"],
  shipping_order: ["shipping", "completed"],
  three_pl: ["pickup_3pl"],
  back_office: ["pre_order", "pending", "advance", "production", "product_transfer", "packing", "inventory_packing", "shipping", "completed", "na", "flagged"],
  super_admin: ["pre_order", "pending", "advance", "production", "product_transfer", "packing", "inventory_packing", "shipping", "completed", "na", "flagged"],
};

const SEV = {
  critical: "bg-red-50 text-red-700 border-red-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  info: "bg-blue-50 text-blue-700 border-blue-200",
};

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useT();
  const qc = useQueryClient();
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/stats")).data });
  const { data: alerts = [] } = useQuery({
    queryKey: ["alerts"],
    queryFn: async () => (await api.get("/alerts")).data,
    // Poll every 60s so back-office sees NA / Flagged alerts within a
    // minute of an order flipping, and so 2h re-reminders reappear
    // automatically without a manual refresh.
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });

  // Track which alert IDs we've already toasted this LOGIN SESSION. Kept
  // for legacy compatibility (session key survives reload) — the toasts
  // themselves were removed Feb 28, 2026 because the top alert strip
  // shows every critical alert already and Back Office called the extra
  // middle-of-screen pop-ups redundant noise.
  const SEEN_KEY = "dashboard_toasted_alert_ids_v1";
  const seenRef = React.useRef(null);
  if (seenRef.current === null) {
    try {
      seenRef.current = new Set(JSON.parse(sessionStorage.getItem(SEEN_KEY) || "[]"));
    } catch (e) { seenRef.current = new Set(); }
  }
  // NOTE: intentional no-op — see comment above. The top strip in Layout.jsx
  // is now the single source of truth for critical-alert visibility.

  const action = useMutation({
    mutationFn: ({ id, a }) => api.post(`/alerts/${id}/action`, { action: a }),
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ["alerts"] });
      toast.success(vars.a === "accept" ? "Alert accepted — cleared from feed" : "Following — will remind in 2h");
      // Clear the toast-throttle for this id so a future reminder re-fires.
      if (seenRef.current) {
        seenRef.current.delete(vars.id);
        try { sessionStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(seenRef.current))); } catch (e) {}
      }
    },
  });

  const importShopify = useMutation({
    mutationFn: () => api.post("/mock/shopify-import"),
    onSuccess: () => { qc.invalidateQueries(); toast.success("New Shopify order imported"); },
  });

  // Once an alert is Accepted it disappears from the Dashboard feed
  // (still tracked in the DB for the 48h stale-accept re-remind logic).
  const visibleAlerts = React.useMemo(
    () => (Array.isArray(alerts) ? alerts.filter((a) => a.status !== "accepted") : []),
    [alerts],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("Dashboard")}</h1>
          <p className="text-sm text-slate-500 mt-1">Welcome, {user.name}. Real-time operations view.</p>
        </div>
        {user.role === "back_office" && (
          <Button onClick={() => importShopify.mutate()} data-testid="import-shopify-btn" className="bg-slate-900 hover:bg-slate-800">
            + Import Shopify Order (mock)
          </Button>
        )}
      </div>

      {/* Inventory Packing counters — Inventory Packing station only (user rule Oct 7, 2026) */}
      {user.role === "inventory_packing" && <InvPackingKpis canConfigure={false} />}

      {/* Stage counts */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="stage-stats">
          {(ROLE_DASHBOARD_STAGES[user.role] || Object.keys(stats.stage_counts)).map((k) => (
            <Link key={k} to={`/orders/${k}`} className="bg-white border border-slate-200 rounded-md p-4 hover:border-slate-400 transition-colors block">
              <div className="text-[15px] font-bold uppercase tracking-wider text-slate-600 font-mono leading-tight" data-testid={`stage-stat-label-${k}`}>{t(STAGE_LABEL[k] || k)}</div>
              <div className="font-display text-[45px] font-bold mt-1 leading-none" data-testid={`stage-stat-count-${k}`}>{stats.stage_counts[k] ?? 0}</div>
            </Link>
          ))}
          {stats.claims_in_progress !== undefined && (
            <Link to="/claims" className={`border rounded-md p-4 transition-colors block ${stats.claims_in_progress ? "bg-red-50 border-red-300 hover:border-red-500" : "bg-white border-slate-200 hover:border-slate-400"}`} data-testid="stage-stat-claims">
              <div className="text-[15px] font-bold uppercase tracking-wider text-red-700 font-mono leading-tight" data-testid="stage-stat-label-claims">⚠️ {t("Claims in progress")}</div>
              <div className="font-display text-[45px] font-bold mt-1 leading-none text-red-800" data-testid="stage-stat-count-claims">{stats.claims_in_progress}</div>
              {stats.claims_open ? <div className="text-[11px] font-mono text-red-700">{stats.claims_open} {t("waiting for decision")}</div> : null}
            </Link>
          )}
          {/* 🔔 Reminders due for this user's role (rule Oct 29, 2026) */}
          <RemindersSquare />
        </div>
      )}

      {/* After-sale claims */}
      <ClaimsPanel user={user} />

      {/* Alerts */}
      <section className="bg-white border border-slate-200 rounded-md">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-semibold">{t("Alert feed")}</h2>
            <p className="text-xs text-slate-500">
              {user.role === "super_admin" ? t("Global alerts for all users.") : t("Alerts targeted to your role.")}
            </p>
          </div>
          <Badge className="bg-slate-100 text-slate-700 border-slate-200">{visibleAlerts.length} {t("open alerts")}</Badge>
        </header>
        <ul data-testid="alerts-list">
          {visibleAlerts.length === 0 && <li className="px-5 py-10 text-center text-sm text-slate-500">{t("No alerts. All stations on time.")}</li>}
          {visibleAlerts.map((a) => {
            const isHold = a.type === "packing_hold";
            const isHoldResolved = a.type === "packing_hold_resolved";
            const isHoldOk = a.type === "hold_accepted";
            const isQc = a.type === "quality_control";
            const isExt = a.type === "extension_request";
            const isReorder = a.type === "reorder_approval";
            const isStore = a.type === "store_in_warehouse" || a.type === "spare_parts";
            const isCancelStop = a.type === "cancel_stock_stop";
            const isHoldStop = a.type === "ship_hold_stop";
            const isFactory = String(user.role || "").startsWith("factory_");
            const showDetail = ["packing_hold", "packing_hold_resolved", "quality_control", "qc_send_back", "hold_accepted", "extension_request", "extension_approved", "production_overdue", "flag_resumed", "reorder_approval", "store_in_warehouse", "spare_parts", "bad_photos", "after_sale_claim", "claim_decided", "production_needed", "cancel_stock_stop", "cancel_stock_pending", "cancel_stock_missing_catalog", "ship_hold_stop", "ship_hold_pending"].includes(a.type);
            const isProdNeeded = a.type === "production_needed";
            return (
            <li key={a.id} className={`px-5 py-3 border-b border-slate-100 flex items-start gap-3 last:border-0 ${a.status === "followed" ? "bg-amber-50/50" : ""} ${isHold ? "bg-amber-50/60" : ""} ${isQc || isProdNeeded ? "bg-red-50/70" : ""} ${isExt ? "bg-sky-50/70" : ""} ${isReorder ? "bg-slate-100/80" : ""} ${isStore ? "bg-teal-50/70" : ""}`} data-testid={`alert-row-${a.id}`}>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border ${SEV[a.severity] || SEV.info}`}>
                {t(a.severity)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium flex items-center gap-2 flex-wrap" data-testid={`alert-headline-${a.id}`}>
                  <span>{alertHeadline(a, t)}</span>
                  {isHoldResolved && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid={`alert-accepted-note-${a.id}`}>
                      ✓ {t("Accepted")} {t("by")} {a.resolved_by || "—"}
                      {a.resolved_at && <span className="text-slate-400 font-normal">· {String(a.resolved_at).slice(0, 16).replace("T", " ")}</span>}
                    </span>
                  )}
                </div>
                {showDetail && (a.detail || a.note) && (
                  <div className="text-xs text-slate-700 mt-0.5" data-testid={`alert-detail-${a.id}`}>
                    {a.detail && <span className="font-semibold">{a.detail}</span>}
                    {a.reporter && <span className="text-slate-500"> · {a.reporter}</span>}
                    {a.note && <span className="italic"> · {t("Response")}: "{a.note}"</span>}
                  </div>
                )}
                {Array.isArray(a.photos) && a.photos.length > 0 && (
                  <div className="mt-1.5"><IssuePhotos photos={a.photos} size="h-14" testid={`alert-photos-${a.id}`} /></div>
                )}
                <div className="text-xs text-slate-500 mt-0.5 font-mono uppercase tracking-wider">
                  {alertTypeLabel(a, t)} · {t("target")}: {a.target_role}
                  {a.status === "followed" && (
                    <span className="ml-2 text-amber-700 normal-case font-sans">
                      {t("Following · reminder in ≤2h")}
                    </span>
                  )}
                </div>
              </div>
              {a.order_id && (
                <Link to={`/order/${a.order_id}`} className="text-xs underline text-slate-700 hover:text-slate-900">
                  {t("View")}
                </Link>
              )}
              {a.claim_id && (
                <Link to={`/claims?claim=${a.claim_id}`} className="text-xs underline text-rose-700 hover:text-rose-900 font-semibold" data-testid={`alert-claim-link-${a.id}`}>
                  {t("Open claim")} ↗
                </Link>
              )}
              {!a.order_id && a.link && (
                <Link to={a.link} className="text-xs underline text-slate-700 hover:text-slate-900" data-testid={`alert-link-${a.id}`}>
                  {t("Open")}
                </Link>
              )}
              <div className="flex gap-2 flex-wrap justify-end">
                {isHold && a.order_id && HOLD_RESPONDER_ROLES.includes(user.role) && (
                  <HoldRespondButtons orderId={a.order_id} poNumber={a.po_number} reorderStatus={a.reorder_status} reorderNote={a.reorder_note} reorderBy={a.reorder_by} />
                )}
                {isReorder && a.order_id && canDecideReorder(user, a.target_role) && (
                  <ReorderDecideButtons orderId={a.order_id} poNumber={a.po_number} />
                )}
                {isStore && a.order_id && canAckStore(user, a.warehouse) && (
                  <StoreAckButton orderId={a.order_id} spare={a.type === "spare_parts"} />
                )}
                {isCancelStop && a.order_id && canConfirmCancelStop(user, a.warehouse) && (
                  <CancelStopButton orderId={a.order_id} due />
                )}
                {isHoldStop && a.order_id && canAckHoldStop(user, a.warehouse) && (
                  <HoldStopButton orderId={a.order_id} due />
                )}
                {isQc && a.order_id && (isFactory || user.role === "super_admin") && (
                  <QcResolveButton orderId={a.order_id} />
                )}
                {isExt && a.order_id && canDecideExtension(user) && (
                  <ExtensionDecideButtons orderId={a.order_id} />
                )}
                {(isHoldResolved || isHoldOk) && (
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => action.mutate({ id: a.id, a: "accept" })}
                    data-testid={`alert-${a.id}-qc-resolved`}
                    disabled={a.status === "accepted"}
                    title={t("Remove this order from the alerts")}
                  >
                    ✓ {t("QC Resolved")}
                  </Button>
                )}
                {!isHold && !isHoldResolved && !isHoldOk && !isQc && !isExt && !isReorder && !isStore && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => action.mutate({ id: a.id, a: "accept" })}
                      data-testid={`alert-${a.id}-accept`}
                      disabled={a.status === "accepted"}
                    >
                      {a.status === "accepted" ? t("Accepted") : t("Accept")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => action.mutate({ id: a.id, a: "follow" })}
                      data-testid={`alert-${a.id}-follow`}
                      disabled={a.status !== "open"}
                    >
                      {a.status === "followed" ? t("Following") : t("Follow")}
                    </Button>
                  </>
                )}
              </div>
            </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
